/**
 * HTML + script à coller sur un site externe : bouton thème Lyrra et lecteur
 * public en modale large. Le script ne s’initialise qu’une fois ; plusieurs
 * boutons (.lyrra-eduflow-embed-btn) peuvent coexister sur la même page.
 */

import { parseSupportedUiLangCode } from '@/lib/ui-languages';

/** postMessage depuis le player (iframe) pour demander la fermeture de la modale parente. */
export const LYRRA_EDUFLOW_PLAYER_REQUEST_CLOSE = 'lyrra-eduflow-request-close';

function escapeHtmlAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtmlText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * URL du player public pour iframe / snippet « Partager » : sommaire replié au chargement.
 * Les autres paramètres de l’URL sont conservés.
 */
export function withEmbedPlayerSearchParams(
  publicPlayerAbsoluteUrl: string,
  options?: { lang?: string },
): string {
  const lang = options?.lang ? parseSupportedUiLangCode(options.lang) : null;
  try {
    const u = new URL(publicPlayerAbsoluteUrl);
    u.searchParams.set('sidebar', '0');
    if (lang) u.searchParams.set('lang', lang);
    return u.href;
  } catch {
    const join = publicPlayerAbsoluteUrl.includes('?') ? '&' : '?';
    const langPart = lang ? `&lang=${encodeURIComponent(lang)}` : '';
    return `${publicPlayerAbsoluteUrl}${join}sidebar=0${langPart}`;
  }
}

/**
 * Indique si l’URL pointe vers le lecteur public EduFlow (lien court /f/… ou chemin /eduflow/flows/…/public).
 * Utilisé sur /aide et /help pour ouvrir la même modale embed que le snippet HTML.
 */
export function isLyrraPublicEduflowPlayerUrl(url: string): boolean {
  if (typeof window === 'undefined') return false;
  const raw = url.trim();
  if (!raw || raw.startsWith('//')) return false;
  try {
    const u = raw.startsWith('/') ? new URL(raw, window.location.origin) : new URL(raw);
    const path = (u.pathname.replace(/\/+$/, '') || '/').toLowerCase();
    if (/^\/f\/[^/]+$/i.test(path)) return true;
    if (/^\/eduflow\/flows\/[^/]+\/public$/i.test(path)) return true;
    return false;
  } catch {
    return false;
  }
}

/** URL absolue pour iframe (chemin relatif résolu sur l’origine courante). */
export function toAbsoluteLyrraPlayerUrlForEmbed(relativeOrAbsolute: string): string {
  const raw = relativeOrAbsolute.trim();
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (typeof window === 'undefined') return raw;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return new URL(path, window.location.origin).href;
}

function injectLyrraEduflowEmbedStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('lyrra-eduflow-embed-styles')) return;
  const P = '#001B3D';
  const H = '#002952';
  const Z = '2147483647';
  const s = document.createElement('style');
  s.id = 'lyrra-eduflow-embed-styles';
  s.textContent =
    '.lyrra-eduflow-embed-btn{font-family:inherit;font-size:15px;font-weight:600;line-height:1.2;padding:12px 22px;border:none;border-radius:10px;cursor:pointer;background:' +
    P +
    ';color:#fff;box-shadow:0 2px 8px rgba(0,0,0,.12);transition:background .15s ease}' +
    '.lyrra-eduflow-embed-btn:hover,.lyrra-eduflow-embed-btn:focus{background:' +
    H +
    ';outline:2px solid #fff;outline-offset:2px}' +
    '#lyrra-eduflow-embed-overlay{position:fixed;inset:0;background:rgba(0, 27, 61,.65);z-index:' +
    Z +
    ';display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box}' +
    '#lyrra-eduflow-embed-dialog{position:relative;width:min(1200px,calc(100vw - 32px));height:min(90vh,calc(100dvh - 32px));background:#001B3D;border-radius:16px;box-shadow:0 25px 50px -12px rgba(0,0,0,.45);overflow:hidden;display:flex;flex-direction:column}' +
    '#lyrra-eduflow-embed-close{position:absolute;top:10px;right:10px;z-index:2;width:40px;height:40px;border:none;border-radius:10px;background:rgba(255,255,255,.12);color:#fff;font-size:22px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center}' +
    '#lyrra-eduflow-embed-close:hover{background:rgba(255,255,255,.2)}' +
    '#lyrra-eduflow-embed-frame{flex:1;border:0;width:100%;min-height:0;background:#fff}';
  document.head.appendChild(s);
}

function detachLyrraEduflowEmbedRoot(root: HTMLElement): void {
  if (root.parentNode) root.parentNode.removeChild(root);
  document.body.style.overflow = '';
}

/**
 * Ouvre le lecteur public EduFlow dans la même modale que le snippet embed (overlay + iframe).
 * Utilisable depuis l’app sans injecter le `<script>` externe.
 */
export function openLyrraEduflowEmbedModal(
  url: string,
  labels: { modalAriaLabel: string; closeAriaLabel: string },
): void {
  if (typeof document === 'undefined') return;
  injectLyrraEduflowEmbedStyles();
  const existing = document.getElementById('lyrra-eduflow-embed-root');
  if (existing) detachLyrraEduflowEmbedRoot(existing as HTMLElement);
  document.body.style.overflow = 'hidden';
  const root = document.createElement('div');
  root.id = 'lyrra-eduflow-embed-root';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', labels.modalAriaLabel);
  const overlay = document.createElement('div');
  overlay.id = 'lyrra-eduflow-embed-overlay';
  const dialog = document.createElement('div');
  dialog.id = 'lyrra-eduflow-embed-dialog';
  const closeBtn = document.createElement('button');
  closeBtn.id = 'lyrra-eduflow-embed-close';
  closeBtn.type = 'button';
  closeBtn.innerHTML = '&times;';
  closeBtn.setAttribute('aria-label', labels.closeAriaLabel);
  const iframe = document.createElement('iframe');
  iframe.id = 'lyrra-eduflow-embed-frame';
  iframe.src = url;
  iframe.title = labels.modalAriaLabel;
  iframe.setAttribute('allowfullscreen', '');
  dialog.appendChild(closeBtn);
  dialog.appendChild(iframe);
  overlay.appendChild(dialog);
  root.appendChild(overlay);
  document.body.appendChild(root);
  const teardown = () => {
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('message', onMessage);
    detachLyrraEduflowEmbedRoot(root);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') teardown();
  };
  const onMessage = (ev: MessageEvent) => {
    const d = ev.data;
    if (
      d &&
      typeof d === 'object' &&
      (d as { type?: string }).type === LYRRA_EDUFLOW_PLAYER_REQUEST_CLOSE
    ) {
      teardown();
    }
  };
  document.addEventListener('keydown', onKey);
  window.addEventListener('message', onMessage);
  closeBtn.onclick = (ev) => {
    ev.stopPropagation();
    teardown();
  };
  overlay.onclick = (ev) => {
    if (ev.target === overlay) teardown();
  };
}

function buildEmbedScript(closeAriaLabel: string, modalAriaLabel: string): string {
  const ca = JSON.stringify(closeAriaLabel);
  const ma = JSON.stringify(modalAriaLabel);
  return `(function () {
  if (window.__lyrraStudioEduflowEmbedInit) return;
  window.__lyrraStudioEduflowEmbedInit = true;
  var P = '#001B3D';
  var H = '#002952';
  var Z = '2147483647';
  function injectStyles() {
    if (document.getElementById('lyrra-eduflow-embed-styles')) return;
    var s = document.createElement('style');
    s.id = 'lyrra-eduflow-embed-styles';
    s.textContent =
      '.lyrra-eduflow-embed-btn{font-family:inherit;font-size:15px;font-weight:600;line-height:1.2;padding:12px 22px;border:none;border-radius:10px;cursor:pointer;background:' +
      P +
      ';color:#fff;box-shadow:0 2px 8px rgba(0,0,0,.12);transition:background .15s ease}' +
      '.lyrra-eduflow-embed-btn:hover,.lyrra-eduflow-embed-btn:focus{background:' +
      H +
      ';outline:2px solid #fff;outline-offset:2px}' +
      '#lyrra-eduflow-embed-overlay{position:fixed;inset:0;background:rgba(0, 27, 61,.65);z-index:' +
      Z +
      ';display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box}' +
      '#lyrra-eduflow-embed-dialog{position:relative;width:min(1200px,calc(100vw - 32px));height:min(90vh,calc(100dvh - 32px));background:#001B3D;border-radius:16px;box-shadow:0 25px 50px -12px rgba(0,0,0,.45);overflow:hidden;display:flex;flex-direction:column}' +
      '#lyrra-eduflow-embed-close{position:absolute;top:10px;right:10px;z-index:2;width:40px;height:40px;border:none;border-radius:10px;background:rgba(255,255,255,.12);color:#fff;font-size:22px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center}' +
      '#lyrra-eduflow-embed-close:hover{background:rgba(255,255,255,.2)}' +
      '#lyrra-eduflow-embed-frame{flex:1;border:0;width:100%;min-height:0;background:#fff}';
    document.head.appendChild(s);
  }
  function closeModal(root) {
    if (root && root.parentNode) root.parentNode.removeChild(root);
    document.body.style.overflow = '';
  }
  function openModal(url) {
    injectStyles();
    var existing = document.getElementById('lyrra-eduflow-embed-root');
    if (existing) closeModal(existing);
    document.body.style.overflow = 'hidden';
    var root = document.createElement('div');
    root.id = 'lyrra-eduflow-embed-root';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', ${ma});
    var overlay = document.createElement('div');
    overlay.id = 'lyrra-eduflow-embed-overlay';
    var dialog = document.createElement('div');
    dialog.id = 'lyrra-eduflow-embed-dialog';
    var closeBtn = document.createElement('button');
    closeBtn.id = 'lyrra-eduflow-embed-close';
    closeBtn.type = 'button';
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute('aria-label', ${ca});
    var iframe = document.createElement('iframe');
    iframe.id = 'lyrra-eduflow-embed-frame';
    iframe.src = url;
    iframe.title = ${ma};
    iframe.setAttribute('allowfullscreen', '');
    dialog.appendChild(closeBtn);
    dialog.appendChild(iframe);
    overlay.appendChild(dialog);
    root.appendChild(overlay);
    document.body.appendChild(root);
    var CLOSE_MSG = 'lyrra-eduflow-request-close';
    function teardown() {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMessage);
      closeModal(root);
    }
    function onKey(e) {
      if (e.key === 'Escape') teardown();
    }
    function onMessage(ev) {
      try {
        var d = ev.data;
        if (d && typeof d === 'object' && d.type === CLOSE_MSG) teardown();
      } catch (err) {}
    }
    document.addEventListener('keydown', onKey);
    window.addEventListener('message', onMessage);
    closeBtn.onclick = function (ev) {
      ev.stopPropagation();
      teardown();
    };
    overlay.onclick = function (ev) {
      if (ev.target === overlay) teardown();
    };
  }
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target && e.target.closest && e.target.closest('.lyrra-eduflow-embed-btn');
      if (!t) return;
      var u = t.getAttribute('data-lyrra-eduflow-url');
      if (!u) return;
      e.preventDefault();
      openModal(u);
    },
    false,
  );
})();`;
}

export function buildLyrraEduflowEmbedSnippet(opts: {
  publicPlayerUrl: string;
  buttonLabel: string;
  closeAriaLabel: string;
  modalAriaLabel: string;
}): string {
  const urlAttr = escapeHtmlAttr(opts.publicPlayerUrl);
  const label = escapeHtmlText(opts.buttonLabel);
  const script = buildEmbedScript(opts.closeAriaLabel, opts.modalAriaLabel);
  return (
    `<!-- Stratelya embed: one <script> per page; copy the button line for more courses (change data-lyrra-eduflow-url) -->\n` +
    `<button type="button" class="lyrra-eduflow-embed-btn" data-lyrra-eduflow-url="${urlAttr}">${label}</button>\n` +
    `<script>\n${script}\n<\/script>`
  );
}
